import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';

interface ResizableSidebarProps {
  children: ReactNode;
  storageKey: string;
  defaultWidthPx?: number;
  collapsedWidthPx?: number;
  minPx?: number;
  maxPx?: number;
  stepPx?: number;
  bigStepPx?: number;
}

const readStoredWidth = (storageKey: string, fallback: number): number => {
  if (typeof window === 'undefined') return fallback;
  try {
    const stored = window.localStorage.getItem(storageKey);
    const parsed = stored === null ? NaN : Number(stored);
    return Number.isFinite(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
};

/**
 * A horizontally-resizable sidebar (drag the right edge left/right). Sibling
 * to ResizableSplit (which handles the vertical Request/Response case) —
 * kept as a separate component rather than a shared `direction` prop so the
 * already-verified vertical split isn't put at risk by a generalization.
 */
export const ResizableSidebar = ({
  children,
  storageKey,
  defaultWidthPx = 280,
  collapsedWidthPx = 220,
  minPx = 200,
  maxPx = 560,
  stepPx = 16,
  bigStepPx = 48,
}: ResizableSidebarProps) => {
  const dragState = useRef<{ startX: number; startWidth: number } | null>(null);
  const [width, setWidth] = useState(() => readStoredWidth(storageKey, defaultWidthPx));
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const clamp = useCallback(
    (value: number) => Math.min(maxPx, Math.max(minPx, value)),
    [minPx, maxPx],
  );

  const persist = useCallback(
    (value: number) => {
      try {
        window.localStorage.setItem(storageKey, String(value));
      } catch {
        // Private browsing / quota exceeded — resizing still works, it just won't persist.
      }
    },
    [storageKey],
  );

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== undefined && event.button !== 0) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    dragState.current = { startX: event.clientX, startWidth: width };
    setIsDragging(true);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragState.current) return;
    const deltaX = event.clientX - dragState.current.startX;
    setWidth(clamp(dragState.current.startWidth + deltaX));
  };

  const endDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragState.current) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    dragState.current = null;
    setIsDragging(false);
    setWidth((current) => {
      persist(current);
      return current;
    });
  };

  const handleDoubleClick = () => {
    const next = clamp(isExpanded ? defaultWidthPx : collapsedWidthPx);
    setIsExpanded((value) => !value);
    setWidth(next);
    persist(next);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    let delta = 0;
    if (event.key === 'ArrowLeft') delta = event.shiftKey ? -bigStepPx : -stepPx;
    else if (event.key === 'ArrowRight') delta = event.shiftKey ? bigStepPx : stepPx;
    else return;

    event.preventDefault();
    setWidth((current) => {
      const next = clamp(current + delta);
      persist(next);
      return next;
    });
  };

  useEffect(() => {
    if (!isDragging) return;
    const previousUserSelect = document.body.style.userSelect;
    const previousCursor = document.body.style.cursor;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    return () => {
      document.body.style.userSelect = previousUserSelect;
      document.body.style.cursor = previousCursor;
    };
  }, [isDragging]);

  // Re-clamp once on mount in case a stored width predates a min/max change.
  const initialized = useRef(false);
  useLayoutEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    setWidth((current) => clamp(current));
  }, [clamp]);

  return (
    <div className="flex h-full shrink-0" style={{ width }}>
      <div className="min-w-0 flex-1 overflow-hidden">{children}</div>
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize sidebar"
        aria-valuenow={Math.round(width)}
        aria-valuemin={minPx}
        aria-valuemax={maxPx}
        tabIndex={0}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onDoubleClick={handleDoubleClick}
        onKeyDown={handleKeyDown}
        style={{ touchAction: 'none' }}
        className={`group relative flex w-2.5 shrink-0 cursor-col-resize select-none items-center justify-center border-x border-black/10 bg-black/[0.02] outline-none transition-colors hover:bg-blue-500/10 focus-visible:bg-blue-500/15 dark:border-white/10 dark:bg-white/[0.02] dark:hover:bg-blue-400/10 dark:focus-visible:bg-blue-400/15 ${
          isDragging ? 'bg-blue-500/15 dark:bg-blue-400/15' : ''
        }`}
      >
        <div
          className={`h-9 w-1 rounded-full bg-black/20 transition-colors group-hover:bg-blue-500/70 dark:bg-white/20 dark:group-hover:bg-blue-400/70 ${
            isDragging ? 'bg-blue-500/70 dark:bg-blue-400/70' : ''
          }`}
        />
      </div>
    </div>
  );
};
